import { Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  FormikCodeField,
  FormikTelField,
  FormikTextField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { useSession } from '@hooks/useSession';

import { useSendAlertCode } from 'generated/alert_recipient_setting.generated';
import { VerifyDeliveryMethod as AlertDeliveryMethod } from 'generated/types';

import { type FormValues } from './AlertRecipientSettingForm';

const messages = defineMessages({
  title: {
    id: 'alertSetupForm.title',
    defaultMessage: 'Setup {type} Alerts',
  },
  email: {
    id: 'alertSetupForm.type.email',
    defaultMessage: 'Email',
  },
  sms: {
    id: 'alertSetupForm.type.sms',
    defaultMessage: 'Text',
  },
  enterCode: {
    id: 'alertSetupForm.enterCode',
    defaultMessage: 'After receiving a six-digit code, enter the code below',
  },
  emailAddress: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email Address',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  sendCode: {
    id: 'GLOBAL.sendCode',
    defaultMessage: 'Send Code',
  },
  verificationCode: {
    id: 'alertSetupForm.verificationCode',
    defaultMessage:
      'A verification code will be sent to the {type} {provided, select, true{you provide} other{on your account}}',
  },
});

export default function AlertVerification() {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { errors, isSubmitting, values } = useFormikContext<FormValues>();

  const [sendCode] = useSendAlertCode();

  const sendAlertCode = async () => {
    const user_id = activeProfile?.user_id;
    if (!values.method || !user_id || !values.destination) {
      return;
    }
    await sendCode({
      variables: {
        input: {
          user_id,
          delivery_method: values.method,
          destination: values.destination,
        },
      },
    });
  };

  return (
    <Stack>
      <Typography>
        {formatMessage(messages.title, {
          type: formatMessage(
            values.method === AlertDeliveryMethod.email
              ? messages.email
              : messages.sms
          ),
        })}
      </Typography>

      <Stack direction="row" justifyContent="flex-start" alignItems="center">
        {values.method === AlertDeliveryMethod.email ? (
          <Stack spacing={0}>
            <FormikTextField
              label={formatMessage(messages.emailAddress)}
              name="destination"
              placeholder="example@vitall.com"
              required
              type="email"
            />
          </Stack>
        ) : (
          <FormikTelField
            label={formatMessage(messages.phone)}
            name="destination"
            required
          />
        )}

        <RoundedButton
          autoSize
          disabled={
            Boolean(errors.destination) ||
            Boolean(errors.method) ||
            isSubmitting
          }
          fontSize={12}
          height={20}
          onClick={sendAlertCode}
        >
          {formatMessage(messages.sendCode)}
        </RoundedButton>
      </Stack>

      <Typography variant="caption">
        {formatMessage(messages.verificationCode, {
          type: formatMessage(
            values.method === AlertDeliveryMethod.email
              ? messages.emailAddress
              : messages.phone
          ).toLowerCase(),
          // If user has email/phone stored in Twilio, display 'on your account'
          // Else, display 'you provide'
          provided: values.method !== AlertDeliveryMethod.email,
        })}
      </Typography>
      <Stack spacing={0}>
        <Typography variant="body2">
          {formatMessage(messages.enterCode)}
        </Typography>
        <FormikCodeField name="code" codeLength={6} />
      </Stack>
      <RoundedButton disabled={isSubmitting} type="submit">
        {formatMessage(messages.title, {
          type: formatMessage(
            values.method === AlertDeliveryMethod.email
              ? messages.email
              : messages.sms
          ),
        })}
      </RoundedButton>
    </Stack>
  );
}
